import React, { useState } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Brain, Zap, FileText, MessageCircle } from 'lucide-react';

const WA_TEMPLATES = [
  { key: 'welcome', label: '👋 Welcome' },
  { key: 'credit_pending_d1', label: '📋 Credit Pending Day 1' },
  { key: 'credit_pending_d4', label: '📋 Credit Pending Day 4' },
  { key: 'proposal_followup', label: '📄 Proposal Follow-up' },
  { key: 'no_answer', label: '📵 No Answer' },
  { key: 'payment_49', label: '💳 ₹49 Payment Link' },
];

export default function AIAnalysisTab({ lead, onUpdate }) {
  const [internalLoading, setInternalLoading] = useState(false);
  const [proposalLoading, setProposalLoading] = useState(false);
  const [fee, setFee] = useState('1.0');
  const [tenure, setTenure] = useState('8');
  const [waTemplate, setWaTemplate] = useState('');
  const [waText, setWaText] = useState('');
  const [waLoading, setWaLoading] = useState(false);
  const [proposalData, setProposalData] = useState(null);

  const runInternalAnalysis = async () => {
    setInternalLoading(true);
    try {
      await api.post(`/ai/internal/${lead.id}`);
      toast.success('Internal analysis complete!');
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Analysis failed');
    } finally {
      setInternalLoading(false);
    }
  };

  const generateProposal = async () => {
    const feeNum = parseFloat(fee);
    if (feeNum < 0.85) {
      toast.error('Fee below 0.85% requires TL approval. Submit approval request first.');
      return;
    }
    if (parseInt(tenure) > 10) {
      toast.error('Tenure above 10 months requires manager approval.');
      return;
    }
    setProposalLoading(true);
    try {
      const r = await api.post(`/ai/proposal/${lead.id}`, { fee: feeNum, tenure: parseInt(tenure) });
      setProposalData(r.data);
      toast.success(`Proposal v${r.data.version} generated!`);
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Proposal failed');
    } finally {
      setProposalLoading(false);
    }
  };

  const loadWATemplate = async (templateType) => {
    setWaTemplate(templateType);
    if (!templateType) { setWaText(''); return; }
    setWaLoading(true);
    try {
      const r = await api.post(`/ai/whatsapp/${lead.id}`, { templateType });
      setWaText(r.data.template);
    } catch (e) {
      toast.error('Template failed');
    } finally {
      setWaLoading(false);
    }
  };

  const sendWhatsApp = () => {
    const encoded = encodeURIComponent(waText);
    window.open(`https://wa.me/91${lead.mobile}?text=${encoded}`, '_blank');
    api.post(`/leads/${lead.id}/activity`, { type: 'whatsapp_template', description: `WhatsApp template sent: ${WA_TEMPLATES.find(t => t.key === waTemplate)?.label}` });
    toast.success('WhatsApp opened');
  };

  const analysis = lead.internalAnalysis;

  return (
    <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 20 }}>
      {/* Left: Internal Analysis */}
      <div>
        <div className="card mb-16">
          <div className="flex-between mb-16">
            <div className="flex-center gap-8">
              <Brain size={18} style={{ color: 'var(--gold)' }} />
              <h3 style={{ fontSize: 15 }}>Internal AI Analysis</h3>
            </div>
            <button className="btn btn-gold btn-sm" onClick={runInternalAnalysis} disabled={internalLoading}>
              {internalLoading ? <span className="spinner" /> : <><Zap size={14} /> Run Analysis</>}
            </button>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12, padding: '8px 10px', background: '#fff7ed', borderRadius: 6, borderLeft: '3px solid var(--warning)' }}>
            ⚠️ This analysis is internal only — never share with client.
          </div>

          {analysis ? (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
                {[
                  { label: 'Enrollment Prob.', value: `${analysis.enrollmentProbability}%`, color: analysis.enrollmentProbability > 60 ? 'var(--success)' : 'var(--warning)' },
                  { label: 'Lead Temp.', value: `${analysis.leadTemperature === 'Hot' ? '🔥' : analysis.leadTemperature === 'Warm' ? '🌤️' : '❄️'} ${analysis.leadTemperature}` },
                  { label: 'Est. Savings', value: analysis.estimatedSavings },
                ].map(s => (
                  <div key={s.label} style={{ textAlign: 'center', padding: '10px 8px', background: 'var(--surface)', borderRadius: 8 }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: s.color || 'var(--navy)' }}>{s.value}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{s.label}</div>
                  </div>
                ))}
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase' }}>Estimated Monthly Fee</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--navy)' }}>₹{(analysis.estimatedMonthlyFee || 0).toLocaleString('en-IN')}</div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase' }}>Top Selling Points</div>
                {analysis.topSellingPoints?.map((p, i) => (
                  <div key={i} style={{ fontSize: 12, padding: '4px 8px', background: '#f0fdf4', borderRadius: 5, marginBottom: 4, color: '#065f46' }}>✓ {p}</div>
                ))}
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase' }}>Recommended Pitch (Hinglish)</div>
                <div style={{ fontSize: 13, padding: '10px 12px', background: 'var(--surface)', borderRadius: 8, lineHeight: 1.6, fontStyle: 'italic' }}>"{analysis.recommendedPitch}"</div>
              </div>

              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase' }}>Next Best Action</div>
                <div style={{ fontSize: 13, padding: '10px 12px', background: '#eff6ff', borderRadius: 8, color: '#1d4ed8', fontWeight: 600 }}>→ {analysis.nextBestAction}</div>
              </div>
            </div>
          ) : (
            <div className="empty-state" style={{ padding: 24 }}>
              <Brain size={32} />
              <h3>No analysis yet</h3>
              <p>Click "Run Analysis" to generate insights</p>
            </div>
          )}
        </div>

        {/* WhatsApp Templates */}
        <div className="card">
          <div className="flex-center gap-8 mb-16">
            <MessageCircle size={18} style={{ color: '#25d366' }} />
            <h3 style={{ fontSize: 15 }}>WhatsApp Templates</h3>
          </div>
          <div className="form-group">
            <label className="form-label">Select Template</label>
            <select className="form-control" value={waTemplate} onChange={e => loadWATemplate(e.target.value)}>
              <option value="">Choose template...</option>
              {WA_TEMPLATES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          {waLoading && <div style={{ textAlign: 'center', padding: 12 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>}
          {waText && (
            <div>
              <textarea className="form-control mb-8" rows={6} value={waText} onChange={e => setWaText(e.target.value)} style={{ fontFamily: 'inherit', fontSize: 13 }} />
              <button className="btn btn-whatsapp w-full" style={{ justifyContent: 'center' }} onClick={sendWhatsApp}>
                <MessageCircle size={14} /> Open in WhatsApp
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Right: Proposal */}
      <div>
        <div className="card mb-16">
          <div className="flex-between mb-16">
            <div className="flex-center gap-8">
              <FileText size={18} style={{ color: 'var(--navy)' }} />
              <h3 style={{ fontSize: 15 }}>Proposal Generator</h3>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Fee % (default 1%, min 0.85%)</label>
            <input className="form-control" type="number" step="0.05" min="0.5" max="3" value={fee} onChange={e => setFee(e.target.value)} />
            {parseFloat(fee) < 0.85 && <div className="text-warning text-sm mt-4">⚠️ Below 0.85% — TL approval required</div>}
          </div>
          <div className="form-group">
            <label className="form-label">Estimated Tenure (months, max 10 without approval)</label>
            <select className="form-control" value={tenure} onChange={e => setTenure(e.target.value)}>
              {[6,7,8,9,10,11,12].map(m => <option key={m} value={m}>{m} months{m > 10 ? ' (requires approval)' : ''}</option>)}
            </select>
          </div>
          <div style={{ padding: '10px 12px', background: 'var(--surface)', borderRadius: 8, marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Estimated Monthly Fee</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--navy)' }}>
              ₹{Math.round((lead.totalLiability || 0) * (parseFloat(fee) / 100)).toLocaleString('en-IN')}
              <span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 400 }}>/month × {tenure} months</span>
            </div>
          </div>
          <button className="btn btn-primary w-full mb-8" style={{ justifyContent: 'center' }} onClick={generateProposal} disabled={proposalLoading}>
            {proposalLoading ? <span className="spinner" /> : <><Zap size={14} /> Generate Proposal</>}
          </button>
        </div>

        {proposalData && (
          <div className="card">
            <div className="flex-between mb-12">
              <h3 style={{ fontSize: 14 }}>Proposal v{proposalData.version}</h3>
              <span className="badge badge-green">Generated</span>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Executive Summary</div>
              <p style={{ fontSize: 13, lineHeight: 1.6 }}>{proposalData.content?.executiveSummary}</p>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>Services Included</div>
              {proposalData.content?.servicesIncluded?.map((s, i) => (
                <div key={i} style={{ fontSize: 12, padding: '3px 0', color: 'var(--text-main)' }}>• {s}</div>
              ))}
            </div>
            <div style={{ padding: '10px 12px', background: '#fff7ed', borderRadius: 8, fontSize: 11, color: '#92400e', lineHeight: 1.6 }}>
              <strong>Disclaimer:</strong> {proposalData.content?.disclaimer}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button className="btn btn-whatsapp btn-sm flex-1" style={{ justifyContent: 'center' }} onClick={() => window.open(`https://wa.me/91${lead.mobile}`, '_blank')}>
                <MessageCircle size={13} /> Send via WhatsApp
              </button>
              <button className="btn btn-ghost btn-sm flex-1" style={{ justifyContent: 'center' }} onClick={() => toast.success('Email feature coming soon')}>
                <Mail size={13} /> Send Email
              </button>
            </div>
          </div>
        )}

        {lead.proposals?.length > 0 && !proposalData && (
          <div className="card">
            <h3 style={{ fontSize: 14, marginBottom: 12 }}>Previous Proposals</h3>
            {lead.proposals.map(p => (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                <span>Version {p.version}</span>
                <span>₹{(p.monthlyFee || 0).toLocaleString('en-IN')}/mo × {p.tenure}mo</span>
                <span className="badge badge-gray">{p.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Needed for send email icon
function Mail({ size }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>;
}
